import { type VehicleManager } from "../../../../managers/VehicleManager";
import { type CompactShape } from "../../../../model/realtime/CompactShape";
import type { StopAnchorWire } from "../../../../types/dtos/realtime-api/realtime.dto";
import { REALTIME_TRANSIT_PATH_SHAPE_SNAP_BACKWARD_TOLERANCE_POINTS } from "./realtimeTransitPath.configuration";

/**
 * Finds the shape point index nearest to a given coordinate within a range.
 *
 * @param shape Full shape point array.
 * @param coord Target [lng, lat] coordinate.
 * @param fromIdx Start of the search range (inclusive).
 * @param toIdx End of the search range (inclusive).
 * @returns Index of the closest shape point within the range.
 */
export function findNearestShapeIndex(
    shape: CompactShape,
    coord: [number, number],
    fromIdx: number,
    toIdx: number,
): number {
    const safeFrom = Math.max( 0, fromIdx );
    const safeTo = Math.min( shape.length - 1, toIdx );
    let bestIdx = safeFrom;
    let bestDist = Infinity;

    for ( let i = safeFrom; i <= safeTo; i += 1 ) {
        const dx = shape.lonAt( i ) - coord[0];
        const dy = shape.latAt( i ) - coord[1];
        const dist = dx * dx + dy * dy;
        if ( dist < bestDist ) {
            bestDist = dist;
            bestIdx = i;
        }
    }

    return bestIdx;
}

/**
 * Resolves the vehicle's leading coordinate from realtime position or timeline fallback.
 *
 * @param vehicleManager Vehicle position resolver.
 * @param tripId Trip identifier.
 * @param nowMs Current timestamp in milliseconds.
 * @returns Leading [lng, lat] or null when unavailable.
 */
export function resolveVehicleLeadingCoord(
    vehicleManager: VehicleManager,
    tripId: string,
    nowMs: number,
): [number, number] | null {
    const realtimePosition = vehicleManager.getRealtimePosition( tripId, nowMs );
    const fallbackCoord = realtimePosition === null
        ? vehicleManager.getTimelineLeadingCoordAtMs( tripId, nowMs )
        : null;

    if ( realtimePosition !== null ) {
        return [ realtimePosition.lon, realtimePosition.lat ];
    }

    if ( fallbackCoord !== null ) {
        return [ fallbackCoord.lon, fallbackCoord.lat ];
    }

    return null;
}

/**
 * Resolves how many stops are already behind the vehicle by comparing schedule time
 * with the vehicle's interpolated position on the trip shape.
 *
 * @param stops Trip stop anchors ordered along the route.
 * @param shapePoints Full trip shape polyline.
 * @param timeBasedNextStopIndex Next stop index from schedule + delay.
 * @param previousStopShapeIdx Shape index of the last passed stop anchor.
 * @param nextStopShapeIdx Shape index of the schedule next stop.
 * @param vehicleManager Vehicle position resolver.
 * @param tripId Trip identifier.
 * @returns Unified next-stop index used to split past vs future markers.
 */
export function resolveEffectiveNextStopIndex(
    stops: readonly StopAnchorWire[],
    shapePoints: CompactShape,
    timeBasedNextStopIndex: number,
    previousStopShapeIdx: number,
    nextStopShapeIdx: number,
    vehicleManager: VehicleManager,
    tripId: string,
): number {
    const leadingCoord = resolveVehicleLeadingCoord( vehicleManager, tripId, Date.now() );

    if ( leadingCoord === null ) {
        return timeBasedNextStopIndex;
    }

    const snapIdx = findNearestShapeIndex(
        shapePoints,
        leadingCoord,
        Math.max( 0, previousStopShapeIdx - REALTIME_TRANSIT_PATH_SHAPE_SNAP_BACKWARD_TOLERANCE_POINTS ),
        nextStopShapeIdx,
    );

    const positionBasedNextStopIndex = stops.findIndex( ( stop ) => stop.shape_idx > snapIdx );

    if ( positionBasedNextStopIndex === -1 ) {
        return Math.max( timeBasedNextStopIndex, stops.length );
    }

    return Math.max( timeBasedNextStopIndex, positionBasedNextStopIndex );
}
